import { useEffect, FC, useMemo } from "react"
import { useQueryClient } from "react-query"

import { Formik, Form, FormikValues, FieldArray} from 'formik'
import * as Yup from 'yup'

import { KTSVG } from "../../../../../../../_metronic/helpers"

import { DLMApplication } from "../../../_models"
import { submitNewDLMApplication, submitDLMPhase_1_2, editDLMApplication } from "../../../_requests"
import { doFormSubmit, isPopulatedArray, processNoteValues } from "../../../../../../components/helpers"
import {
  StatusErrorMessage,
  FormikTextInput, FormikTextField, FormikFileUpload,
  FileUploaderResponse,
  FormikAcknowledgeFeeCheck,
  FormikNotesArea
} from "../../../../../../components/widgets"

import { AttachmentCard } from "../../../../attachments/AttachmentCard"

import { cloneDeep, pickBy } from "lodash"
import { SubmitButtonRow } from "../../../../../../components/layout"
import { DOCHourRatingDropdown, SCDFPlanNoSchema } from "../../.."
import { IWebFileAttachment } from "../../../../applications"

interface IDOCEditModel {
  projectName: string
  projectLocation: string
  bpNumber: string
  installerName: string
  coverageArea: number
  unit: string
  otherReference: string
  hourRating: string
  otherAttachments: Array<IWebFileAttachment>
  comment: string
  note: string
  noteAttachments: Array<IWebFileAttachment>
  acknowledgeFee: boolean
}

const docEditSchema: any = Yup.object({
  projectName: Yup.string().trim().label('Project Name').required(),
  projectLocation: Yup.string().trim().label('Project Location').required(),
  bpNumber: SCDFPlanNoSchema,
  installerName: Yup.string().trim().label('Installer Company').required(),
  coverageArea: Yup.number().label('Area of Coverage'),
  unit: Yup.string().trim().label('Unit of Measure').required(),
  otherReference: Yup.string().trim().label('Other Reference'),
  hourRating: Yup.string().trim().matches(/^(?!0$)(?!0\.0$)(-|\d+(\.\d{1})?)$/, "Select from options.").required().label('Hour Rating'),
  otherAttachments: Yup.array(),
  comment: Yup.string().trim().label('Comment').required(),
  note: Yup.string().trim().label("Notes"),
  noteAttachments: Yup.array(),
  acknowledgeFee: Yup.bool().oneOf([true], 'Must be checked').label('Acknowledge Fee')
});


const initDOCEditFormValues: IDOCEditModel = {
  projectName: '',
  projectLocation: '',
  bpNumber: '',
  installerName: '',
  coverageArea: 0,
  unit: '',
  otherReference: '',
  hourRating: '-',
  otherAttachments: [],
  comment: '',
  note: '',
  noteAttachments: [],
  acknowledgeFee: false
}

const DOCEDIT_FIELDS = [
  'projectName',
  'projectLocation',
  'bpNumber',
  'installerName',
  'coverageArea',
  'unit',
  'hourRating',
  'otherReference',
];

type Props = {
  docId: number
  docNo: string
  appData: DLMApplication
  doClose: Function
  onSuccess: Function
  appId?: number
  resubmitMode? : string
  acceptFiles?: string
}

const DLMDOCEditModal:FC<Props> = ({docId, docNo, appData, doClose, onSuccess, appId, resubmitMode = ''}) => {

  const queryClient = useQueryClient();

  const { initValues, siteId } = useMemo(() => {

    let _init = cloneDeep(initDOCEditFormValues);
    let siteId = 0;

    if (! appData) return { initValues: _init, siteId} ;

    Object.assign(_init, pickBy(appData, (value, key) => (value && DOCEDIT_FIELDS.includes(key))));


    if (appData?.siteApplications && isPopulatedArray(appData.siteApplications)){
      let site = appData.siteApplications[0];
      Object.assign(_init, pickBy(site, (value, key) => (value && (key === 'id' || DOCEDIT_FIELDS.includes(key)))));
      siteId = site?.id ? site.id : 0;
    }

    if (appData?.otherAttachments && isPopulatedArray(appData.otherAttachments) ) {
      Object.assign(_init, { otherAttachments: appData.otherAttachments.map( file => ({id: file.id, name: file.fileName}))});
    }

    return { initValues: _init, siteId};

  }, [appData]);

  const submitFunc = useMemo(() => {
    switch(resubmitMode) {
    case 'resubmit':
      return submitDLMPhase_1_2;
    case 'edit':
      return editDLMApplication;
    default:
      return submitNewDLMApplication;
    }

  }, [resubmitMode]);

  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;

    let _site = pickBy(values, (value, key) => value && DOCEDIT_FIELDS.includes(key));

    if ( resubmitMode && siteId) {
      Object.assign(_site, { id : siteId});
    }

    let _payload = {
      ...pickBy(values, (value, key) => (value && (! (key.match(/^note/i) || key === 'acknowledgeFee')))),
      targetDOC: docId,
      applicationType: "TYPE_DOCEDIT",
      dlmWebPhase0SitesModel: [ _site ],
    };

    if (values.hasOwnProperty('otherAttachments') && values.otherAttachments.length > 0) {
      Object.assign(_payload, {otherAttachments: values.otherAttachments.map((o: any) => (o.id))});
    }

    if (appId) {
      Object.assign(_payload, { id: appId});
    }

    if (resubmitMode) {
      // @ts-ignore
      _payload = { application: _payload };
      processNoteValues(values, _payload);
    }

    await doFormSubmit(submitFunc, _payload, setStatus, setSubmitting, (res) => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'doc-applications-list' } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      if (appId) {
        queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appId } ]);
      }
      if (res?.data) onSuccess(res.data);
    });

  }

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
  <>
  <div
    className='modal fade show d-block'
    id='kt_modal_doc_edit'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >
    <div className='modal-dialog modal-dialog-centered mw-600px'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2 className='fw-bolder'>Edit DOC</h2>
          <div
            className='btn btn-icon btn-sm btn-active-icon-primary'
            data-kt-applicants-modal-action='close'
            onClick={()=> { doClose()}}
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>
        <div className='modal-body scroll-y mx-1 mb-7'>
          <Formik
            initialValues={initValues}
            validationSchema={docEditSchema}
            enableReinitialize={true}
            validateOnMount={true}
            initialTouched={{
              projectName: true,
              projectLocation: true,
              installerName: true,
              hourRating: true,
              comment: true,
              unit: true,
            }}
            onSubmit={doSubmit}
          >
          {({status, values}) => (
          <Form id='kt_docedit_modal_form' className='form' noValidate>

            <h2 className='fw-bolder mb-4'>{`${docNo}`}</h2>

            <div className="row mb-2">
              <FormikTextInput size="12" label="Installer Company" name="installerName" required={true} />
            </div>
            <div className="row mb-2">
              <FormikTextField size="12" label="Project Name" name="projectName" required={true} rows={2} />
              <FormikTextField size="12" label="Project Location" name="projectLocation" required={true} rows={2} />
            </div>

            <div className="row mb-2">
              <FormikTextInput size="12 col-md-6" label="SCDF Building Plan No" name="bpNumber" />
              <FormikTextInput size="6 col-md-3" label="Coverage Area" name="coverageArea" readOnly />
              <FormikTextInput size="6 col-md-3" label="Unit" name="unit" required={true} />
            </div>
            <div className="mb-2 row">
              <FormikTextInput size="12 col-md-8" label="Other Reference Number (DSTA Number)" name="otherReference"/>
              <DOCHourRatingDropdown size="6 col-md-4" label="Hour Rating" name="hourRating" required={true} />
            </div>
            <div className="mb-2 row">
              <FormikFileUpload size="6" label="Other Attachments" name="otherAttachments" acceptFiles="ApplicantFileFormats" isFileArray={true} />
            </div>
            <FieldArray
              name="otherAttachments"
              render={arrayHelpers => (
              <div className="d-flex flex-row flex-wrap gap-2 mh-80px overflow-auto">
              { (values.otherAttachments && values.otherAttachments.length > 0) &&
              values.otherAttachments.map( (o: FileUploaderResponse, idx: number) => (
                <AttachmentCard
                  key={`otherAttachment${idx}`}
                  className="border py-1"
                  idx={idx}
                  fileId={o.id}
                  fileName={o.name}
                  onRemove={(e: any) => {
                    e.stopPropagation();
                    const elem = e.target.closest('.attachment-card');
                    if (elem) {
                      const {idx} = elem.dataset;
                      arrayHelpers.remove(idx);
                    }
                  }}
                />
              ))
              }
              </div>
              )}
            />

            <div className="row mb-2 mt-4">
              <FormikTextField size="12" label="Reason for Editing" name="comment" required={true} rows={2} />
            </div>
            { resubmitMode &&
            <>
            <div className='separator border-gray-200 my-4'></div>
            <FormikNotesArea />
            </>
            }

            <FormikAcknowledgeFeeCheck dlmtype="DOC" />

            <StatusErrorMessage status={status} />
            <SubmitButtonRow doClose={() => doClose()} />
          </Form>
          )}
          </Formik>
        </div>
      </div>
    </div>
  </div>
  <div className='modal-backdrop fade show'></div>
  </>
  )
}

export {DLMDOCEditModal}